import {
    makeItemsListUrl,
    trans,
} from '@frontend/helpers'
import type {
    AdCategory,
    FilteredItemsPageUrlQueryParams,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {useAdsStore} from '@frontend/stores'
import {mdiChevronRight} from '@mdi/js'
import {
    type ReactNode,
    useEffect,
    useState,
} from 'react'
import {Link} from 'react-router-dom'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'

interface Props {
    categoryId: number | null
    services?: boolean
    // Количество подкатегорий, отображаемых в панели фильтрации.
    // Остальные подкатегории скрыты, пока не нажата кнопка "Показать все".
    subCategoriesLimit?: number
    urlQueryParams?: Partial<FilteredItemsPageUrlQueryParams>
}

// Навигация по подкатегориям.
// Отображает родительскую категорию, текущую категорию и подкатегории.
export function AdCategoryFiltersSubcategoriesNav(props: Props) {

    const {
        categoryId,
        services,
        subCategoriesLimit = 8,
        urlQueryParams,
    } = props

    const categories = useAdsStore(
        state => state.categories
    )

    // Категория и ее родственники.
    const [
        category,
        setCategory,
    ] = useState<AdCategory | null>()
    const [
        parentCategories,
        setParentCategories,
    ] = useState<AdCategory[]>([])
    const [
        subCategories,
        setSubCategories,
    ] = useState<AdCategory[]>([])
    const [
        showAllSubCategories,
        setShowAllSubCategories,
    ] = useState<boolean>(false)

    // Поиск подкатегорий текущей категории.
    useEffect(() => {
        if (categories.length > 0) {
            if (!categoryId) {
                setSubCategories(
                    services ? getServicesSubcategories(categories) : categories
                )
                return
            }

            const result = findCategoryAndParents(
                categories,
                categoryId
            )
            if (result) {
                setCategory(result.category)
                setSubCategories(result.category.children ?? [])
                setParentCategories(result.parents ?? [])
            } else {
                setCategory(null)
                setSubCategories([])
                setParentCategories([])
            }
            setShowAllSubCategories(false)
        }
    }, [categories, categoryId])

    if (subCategories.length === 0 && parentCategories.length === 0) {
        return null
    }

    // Родительские категории.
    const parents = []
    for (const item of parentCategories) {
        parents.push(
            <li key={'parent-category-' + item.id}>
                <Link
                    to={makeItemsListUrl(item, urlQueryParams)}
                    className="link-dark with-icon-flex gap-2"
                >
                    {item.name}
                    <Icon
                        path={mdiChevronRight}
                        size={20}
                    />
                </Link>
            </li>
        )
    }

    // Подкатегории.
    const children = []
    for (const item of subCategories) {
        children.push(
            <li key={'subcategory-' + item.id}>
                <Link
                    to={makeItemsListUrl(item, urlQueryParams)}
                    className="link-dark d-inline-block lh-sm"
                >
                    {item.name}
                </Link>
            </li>
        )
    }

    // Скрытые подкатегории.
    // Не применяется к корневым категориям (categoryId === null).
    let hiddenItems: ReactNode[] = []
    if (categoryId && children.length > subCategoriesLimit) {
        hiddenItems = children.splice(subCategoriesLimit)
    }

    return (
        <Collapse
            show={
                categories.length > 0
                && (subCategories.length > 0 || parentCategories.length > 0)
            }
            showImmediately
        >
            <div className="offered-items-filters-subcategories pb-4">
                <ul className="list-unstyled my-0 d-flex flex-column gap-2">
                    {category && (
                        <>
                            {parents}
                            <li className="text-muted">
                                {category.name}
                            </li>
                        </>
                    )}
                    {children}
                </ul>
                {hiddenItems.length > 0 && (
                    <Collapse show={showAllSubCategories}>
                        <ul className="list-unstyled my-0 pt-2 d-flex flex-column gap-2">
                            {hiddenItems}
                        </ul>
                    </Collapse>
                )}
                <Collapse
                    show={hiddenItems.length > 0 && !showAllSubCategories}
                    showImmediately
                >
                    <div>
                        <Button
                            color="link"
                            onClick={() => setShowAllSubCategories(true)}
                            className="link-blue mt-2"
                        >
                            {trans().ads.list.filters.show_all_options} (+{hiddenItems.length})
                        </Button>
                    </div>
                </Collapse>
                {category && (
                    <>
                        <hr className="my-4" />
                        <div className="fs-5 fw-700">
                            {category.name}:
                        </div>
                    </>
                )}
            </div>
        </Collapse>
    )
}

// Поиск текущей категории в дереве категорий.
function findCategoryAndParents(categories: AdCategory[], categoryId: number): {
    category: AdCategory
    parents: AdCategory[]
} | null {
    for (const category of categories) {
        if (category.id === categoryId) {
            return {category, parents: []}
        }
        if (category.children && category.children.length > 0) {
            const subCategory = findCategoryAndParents(
                category.children,
                categoryId
            )
            if (subCategory) {
                subCategory.parents.unshift(category)
                return {category: subCategory.category, parents: subCategory.parents}
            }
        }
    }
    return null
}

// Поиск подкатегорий услуг.
function getServicesSubcategories(categories: AdCategory[]): AdCategory[] {
    return categories.find(category => category.type === 'services')?.children ?? []
}
